import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AssetManager } from '@app/asset-manager/asset-manager';
import { GraphicsRenderRepair } from '@app/digital-twin/graphics/graphics-render-repair';
import { ElementTypeOptionsUtil } from '../../utils/element-type-options.util';

/**
 * Shared "Structure Type" selection list for the insertion flows (repair, critical area,
 * element insertion, inspection). Builds the list of element types visible in the scene,
 * defaults to the currently selected element's type, and writes the choice to
 * assetManager.elementTypeForInsertionAndSelection (read by the insertion engines at
 * draw time).
 *
 * Optionally renders the front-facing projection toggle shared by the area-drawing tools
 * (GraphicsRenderRepair.useOrthoProjection).
 */
@Component({
  selector: 'phdsoft-element-type-list',
  templateUrl: './element-type-list.component.html',
  styleUrls: ['./element-type-list.component.scss'],
})
export class ElementTypeListComponent implements OnInit, OnDestroy {
  @Input() showProjectionToggle = false;
  /**
   * Multi-selection mode ("Select more" in the properties window): each type is an
   * on/off toggle bound to assetManager.multiSelectAllowedTypes. No insertion-type
   * default, no write to elementTypeForInsertionAndSelection, no projection sync.
   */
  @Input() multiple = false;

  elementTypes: string[] = [];
  selectedElementType = '';

  private selectionSubscription?: Subscription;

  constructor(private assetManager: AssetManager) {}

  ngOnInit(): void {
    if (this.multiple) {
      const seeded = this.assetManager.multiSelectAllowedTypes ?? [];
      const visible = ElementTypeOptionsUtil.getVisibleStructureElementTypes(this.assetManager.asset);
      this.elementTypes = ElementTypeOptionsUtil.sortTypes(Array.from(new Set([...visible, ...seeded])));
      return;
    }
    this.elementTypes = ElementTypeOptionsUtil.getVisibleElementTypes(this.assetManager.asset);

    // default to the currently selected element's type, falling back to the first option
    let defaultType = '';
    if (this.assetManager.currentSelectedElement) {
      defaultType = ElementTypeOptionsUtil.mapElementTypeByTypeId(this.assetManager.currentSelectedElement.TypeId);
    }
    if (!defaultType || !this.elementTypes.includes(defaultType)) {
      defaultType = this.elementTypes.length > 0 ? this.elementTypes[0] : 'Plate';
    }
    this.selectElementType(defaultType);

    // React to viewer selection changes (select/deselect) so the projection value never
    // lingers after the matching element is deselected — the div hides via the getter, but
    // the shared GraphicsRenderRepair.useOrthoProjection must be reset too.
    this.selectionSubscription = this.assetManager.currentSelectedElementChanged.subscribe(() => {
      this.syncProjectionValue();
    });
  }

  ngOnDestroy(): void {
    this.selectionSubscription?.unsubscribe();
  }

  /** Multiple mode: whether `type` is currently allowed. */
  isSelected(type: string): boolean {
    return this.multiple
      ? (this.assetManager.multiSelectAllowedTypes ?? []).includes(type)
      : this.selectedElementType === type;
  }

  /** Multiple mode: toggles `type` on/off; the last allowed type cannot be turned off. */
  toggleType(type: string): void {
    const current = this.assetManager.multiSelectAllowedTypes ?? [];
    if (current.includes(type)) {
      if (current.length <= 1) {
        return;
      }
      this.assetManager.multiSelectAllowedTypes = current.filter(t => t !== type);
    } else {
      this.assetManager.multiSelectAllowedTypes = ElementTypeOptionsUtil.sortTypes([...current, type]);
    }
  }

  onTypeClick(type: string): void {
    if (this.multiple) {
      this.toggleType(type);
    } else {
      this.selectElementType(type);
    }
  }

  selectElementType(type: string): void {
    this.selectedElementType = type;
    this.assetManager.elementTypeForInsertionAndSelection = type;
    this.syncProjectionValue();
  }

  // Front-facing projection: when on, the drawn area is generated as if the camera were
  // directly in front of the element (no distortion at oblique camera angles).
  // Session-only setting shared by all area-drawing tools; applies to the next drawn area.
  get useFrontProjection(): boolean {
    return GraphicsRenderRepair.useOrthoProjection;
  }

  setUseFrontProjection(value: boolean): void {
    GraphicsRenderRepair.useOrthoProjection = value;
  }

  /**
   * The projection toggle is shown only for projection types (Stiffener/Bracket/FacePlate),
   * when the host flow enables it and an element of that same type is selected in the viewer.
   */
  get isProjectionToggleVisible(): boolean {
    return ElementTypeOptionsUtil.resolveProjection(
      this.showProjectionToggle,
      this.selectedElementType,
      this.hasMatchingSelectedElement,
    ).toggleVisible;
  }

  /** True when the element selected in the viewer is of the currently chosen Structure Type. */
  private get hasMatchingSelectedElement(): boolean {
    const element = this.assetManager.currentSelectedElement;
    return !!element
      && ElementTypeOptionsUtil.mapElementTypeByTypeId(element.TypeId) === this.selectedElementType;
  }

  /**
   * Keeps GraphicsRenderRepair.useOrthoProjection consistent with the current selection:
   * non-projection types always project front-on (true); projection types default to off (the
   * user opts in via the visible toggle). Left untouched when the flow disables projection.
   */
  private syncProjectionValue(): void {
    const { valueToApply } = ElementTypeOptionsUtil.resolveProjection(
      this.showProjectionToggle,
      this.selectedElementType,
      this.hasMatchingSelectedElement,
    );
    if (valueToApply !== null) {
      this.setUseFrontProjection(valueToApply);
    }
  }
}
